import React, { useState, useEffect, useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { Visualiser } from "@/components/memory/Visualiser";
import { useFirebase } from "@components/firebase/useFirebase";
import { getMyMemories } from "@/services/firebase/memory-functions";
import { getMyMedia } from "@/services/firebase/media-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { MemoryModel, MediaModel, Contact, GroupModel } from "common247";

/**
 * Visualiser screen displaying relational D3 visualization of memories, media,
 * contacts, and groups with pan/zoom and filter support.
 */
export default function MemoryVisualiserScreen() {
  const router = useRouter();
  const services = useFirebase();

  const [memories, setMemories] = useState<MemoryModel[]>([]);
  const [media, setMedia] = useState<MediaModel[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAllData = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const [memsRes, mediaRes, contactsRes, groupsRes] = await Promise.all([
        getMyMemories(services, {}),
        getMyMedia(services, {}),
        getMyContacts(services, {}),
        getMyGroups(services, {}),
      ]);

      if (memsRes.status && memsRes.memories) {
        setMemories(memsRes.memories);
      }
      if (mediaRes.status && mediaRes.media) {
        setMedia(mediaRes.media);
      }
      if (contactsRes.status && contactsRes.contacts) {
        setContacts(contactsRes.contacts);
      }
      if (groupsRes.status && groupsRes.groups) {
        setGroups(groupsRes.groups);
      }
    } catch (err) {
      console.error("[VisualiserScreen] Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  return (
    <ThemedScreen
      title="Memories Visualiser"
      loading={loading}
      scrollable={true}
      rightIcon={{
        name: "list-outline",
        onPress: () => router.push("/my-memories"),
      }}
    >
      <View style={styles.container}>
        <Visualiser
          allMemories={memories}
          allMedia={media}
          allContacts={contacts}
          allGroups={groups}
          onSelectMemory={(m) => router.push(`/my-memories/${m.id}`)}
        />
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 650,
  },
});
